import { Button, Image, Skeleton, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import BookingData, { BookingPax, BookingRoom, Extra, PaxTypeEnum } from '../../../models/BookingData';
import { useEffect, useState } from 'react';
import moment from 'moment';
import HTMLReactParser from 'html-react-parser';


import './BookingDetailPage.scss'
import { CancellationPolicies, Occupation } from '../../../components';
import { CreditCardOutlined, EyeFilled, EyeInvisibleOutlined, EyeOutlined, LoadingOutlined, ZoomInOutlined } from '@ant-design/icons';
import { useAppConfig } from '../../../Contexts/AppConfigContext';
import { CompleteBookingPayment, GetPCIProxyTransactionIdByAliasToken } from '../../../api/repositoryEP';
import ShowCardPCIProxy from '../../../components/PCIProxy/ShowCardPCIProxy';
import { CompleteBookingPaymentRequest } from '../../../models/BookingInfo';

interface BookingDetailPageProps {
    bookingData?: BookingData;
    setBookingData : (bookingData : BookingData) => void
    isGettingBookingData: boolean;
}
const BookingDetailPage = (props: BookingDetailPageProps): JSX.Element => {


    const appConfig = useAppConfig();

    const { bookingData, setBookingData, isGettingBookingData } = props;

    const { t } = useTranslation();


    /**Añadimos un manejador para que cuando se pinche sobre un elemento con la clase resume-link se haga scrollo hasta el elemento con el atributo tag que contenga el nombre del id que tiene resume-link */
    useEffect(() => {
        console.log(bookingData)
        const resumeLinks = document.querySelectorAll(".resume-link");

        resumeLinks.forEach((resumeLink: any) => {
            const handleClick = (event: MouseEvent) => {
                event.preventDefault();
                const tag = (event.currentTarget as HTMLAnchorElement)?.getAttribute(
                    "id"
                );
                const element = document.querySelector(`[data-tag='${tag}']`);
                if (element) {
                    element.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                        inline: "nearest"
                    });
                }
            };

            resumeLink.addEventListener("click", handleClick);

            return () => {
                resumeLink.removeEventListener("click", handleClick);
            };
        });
    }, []);

    useEffect(() => {
        // cada vez que cambia la booking → reset
        setTransactionId('');
        setIsBankDataVisible(false);
        setIsLoadingBankData(false);
    }, [bookingData?.id]);




    const getTotals = (bookingData: BookingData | undefined) => {
        if (!bookingData) return { pvp: 0, net: 0, commission: 0 };

        const totals = bookingData.bookingLines.reduce(
            (acc, room) => {
                acc.pvp += room.pvp;
                acc.net += room.net;
                acc.commission += room.commission;
                return acc;
            },
            { pvp: 0, net: 0, commission: 0 }
        );

        return totals;
    };

    const calculateBookingValues = (bookingData: BookingData | undefined) => {
        const isNetPrice = bookingData?.isNetPrice ?? false;

        const { pvp, net, commission } = getTotals(bookingData);

        return { net, pvp, commission, isNetPrice };
    };

    const { net, pvp, commission, isNetPrice } = calculateBookingValues(bookingData);

    const mapPaxType = (value: any): PaxTypeEnum => {
        switch (value) {
            case 0: return PaxTypeEnum.adult;
            case 1: return PaxTypeEnum.teenager;
            case 2: return PaxTypeEnum.child;
            case 3: return PaxTypeEnum.infant;
            default: return PaxTypeEnum.adult;
        }
    };
    function getBookingStatusText(status: string): string {
        switch (status) {
            case 'canceled':
                return t('components.bookings.list.booking-status.cancelled');
            case 'active':
                return t('components.bookings.list.booking-status.active');
            case 'pre':
                return t('components.bookings.list.booking-status.pre');
            case 'pending_payment':
                return t('components.bookings.list.booking-status.pending_payment');
            case 'error':
                return t('components.bookings.list.booking-status.error');
            case 'postponed':
                return t('components.bookings.list.booking-status.postponed');
            default:
                return t('components.bookings.list.booking-status.pending');
        }
    }

    const [transactionId, setTransactionId] = useState<string>('');
    const [isBankDataVisible, setIsBankDataVisible] = useState(false);
    const [isLoadingBankData, setIsLoadingBankData] = useState(false);
    function handleToggleBankData() {
        if (!isBankDataVisible) {
            if (bookingData && bookingData.cardToken && bookingData.cvvToken) {
                setIsLoadingBankData(true);
                GetPCIProxyTransactionIdByAliasToken(
                    bookingData.cardToken,
                    bookingData.cvvToken
                ).then((r) => {
                    if (r && r.type === 1 && r.data) {
                        setTransactionId(r.data.transactionId);
                        setIsBankDataVisible(true);
                    }
                }).finally(() => setIsLoadingBankData(false));
            }
        } else {
            setTransactionId('');
            setIsBankDataVisible(false);
        }
    }
    const [loadingCompletePayment, setLoadingCompletePayment] = useState<boolean>(false);

    /** Returns value if truthy and not a meaningless placeholder, else null */
    const safe = (val: any): string | null => {
        if (val === null || val === undefined) return null;
        const s = String(val).trim();
        if (s === '' || s === 'undefined' || s === 'null') return null;
        // Catch patterns like "(undefined) undefined", "undefined undefined"
        if (/^(\(undefined\)|\(null\))?\s*(undefined|null)\s*$/i.test(s)) return null;
        // Catch API "not present" placeholders (e.g. "Persona de contacto no presente")
        if (/no presente$/i.test(s) || /not present$/i.test(s)) return null;
        return s;
    };

    /** Like safe() but also requires the value to be a valid http(s) URL */
    const safeUrl = (val: any): string | null => {
        const s = safe(val);
        if (!s) return null;
        if (!s.startsWith('http://') && !s.startsWith('https://')) return null;
        return s;
    };

    function completePayment() {
        if (bookingData) {
            setLoadingCompletePayment(true)
            const data: CompleteBookingPaymentRequest = {
                bookingId: bookingData?.id,
            };

            CompleteBookingPayment(data)
                .then((r) => {
                    if (r && r.type === 1 && r.data === true) {
                            setBookingData({
                        ...bookingData,
                        statusName: 'active'
                    });
                    }
                })
                .finally(() => setLoadingCompletePayment(false)
                );
        }
    }

    return (
        <div className="booking-detail-page">
            {isGettingBookingData && (
                <div className="bdr-skeleton">
                    {/* header */}
                    <Skeleton.Input active block style={{ height: 28, marginBottom: 8 }} />
                    <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                        <Skeleton.Button active size="small" style={{ width: 90 }} />
                        <Skeleton.Button active size="small" style={{ width: 70 }} />
                    </div>
                    {/* two-col grid */}
                    <div className="bdr-skeleton-grid">
                        <div className="bdr-card">
                            <Skeleton active paragraph={{ rows: 3 }} title={{ width: '50%' }} />
                        </div>
                        <div className="bdr-card">
                            <Skeleton active paragraph={{ rows: 3 }} title={{ width: '50%' }} />
                        </div>
                    </div>
                    {/* owner */}
                    <div className="bdr-card">
                        <Skeleton active paragraph={{ rows: 2 }} title={{ width: '40%' }} />
                    </div>
                    {/* economic */}
                    <div className="bdr-card">
                        <Skeleton active paragraph={{ rows: 4 }} title={{ width: '40%' }} />
                    </div>
                    {/* rooms */}
                    <div className="bdr-card" style={{ display: 'flex', gap: 12 }}>
                        <Skeleton.Image active style={{ width: 150, height: 120 }} />
                        <div style={{ flex: 1 }}>
                            <Skeleton active paragraph={{ rows: 3 }} title={{ width: '60%' }} />
                        </div>
                    </div>
                </div>
            )}
            {!isGettingBookingData && (
                <div className="bdr-content">

                    {/* ── Pending payment banner ───────────────────────── */}
                    {bookingData?.statusName === 'pending_payment' && (
                        <div className="bdr-pending-banner">
                            <CreditCardOutlined />
                            <span>{t('pages.booking-detail.pending-payment')}</span>
                        </div>
                    )}

                    {/* ── Header: hotel name + status + locators ─────────── */}
                    <div className="bdr-header">
                        <div className="bdr-header-main">
                            <h2 className="bdr-hotel-name">{bookingData?.hotelInfo.name}</h2>
                            <div className="bdr-locators">
                                {bookingData?.pmsLocator && (
                                    <span className="bdr-locator-chip">
                                        <span className="bdr-locator-label">{t('pages.booking-detail.locator')}</span>
                                        <Typography.Text copyable={{ text: bookingData.pmsLocator }}>
                                            {bookingData.pmsLocator}
                                        </Typography.Text>
                                    </span>
                                )}
                                {bookingData?.proLocator && (
                                    <span className="bdr-locator-chip">
                                        <span className="bdr-locator-label">{t('pages.booking-detail.pro-locator')}</span>
                                        <Typography.Text copyable={{ text: bookingData.proLocator }}>
                                            {bookingData.proLocator}
                                        </Typography.Text>
                                    </span>
                                )}
                            </div>
                        </div>
                        {bookingData?.statusName !== 'pending_payment' && (
                            <span className={`bdr-status-badge bdr-status-${bookingData?.isCancelled ? 'canceled' : bookingData?.statusName}`}>
                                {getBookingStatusText(bookingData?.statusName ?? '')}
                            </span>
                        )}
                    </div>

                    {/* ── Info grid: hotel + booking dates ────────────────── */}
                    <div className="bdr-grid-2">
                        <div className="bdr-card">
                            <div className="bdr-card-title">{t('pages.booking-detail.hotel-address').replace(':', '') || 'Hotel'}</div>
                            <div className="bdr-fields">
                                <div className="bdr-field">
                                    <span className="bdr-field-label">{t('pages.booking-detail.hotel-address')}</span>
                                    <span className="bdr-field-value">{safe(bookingData?.hotelInfo.address) ?? '-'}</span>
                                </div>
                                <div className="bdr-field">
                                    <span className="bdr-field-label">{t('pages.booking-detail.hotel-phone')}</span>
                                    <span className="bdr-field-value">{safe(bookingData?.hotelInfo.phone) ?? '-'}</span>
                                </div>
                                <div className="bdr-field">
                                    <span className="bdr-field-label">{t('pages.booking-detail.hotel-website')}</span>
                                    <span className="bdr-field-value">
                                        {safeUrl(bookingData?.hotelInfo.website)
                                            ? <a className="app-link" href={bookingData!.hotelInfo.website} target="_blank" rel="noreferrer">{bookingData!.hotelInfo.website}</a>
                                            : '-'}
                                    </span>
                                </div>
                                <div className="bdr-field">
                                    <span className="bdr-field-label">{t('pages.booking-detail.hotel-director')}</span>
                                    <span className="bdr-field-value">{safe(bookingData?.hotelInfo.director) ?? '-'}</span>
                                </div>
                            </div>
                        </div>

                        <div className="bdr-card">
                            <div className="bdr-card-title">{t('pages.booking-detail.main-title')}</div>

                            {/* Row 1: fecha de creación + fecha de cancelación */}
                            <div className="bdr-dates-grid">
                                <div className="bdr-field">
                                    <span className="bdr-field-label bdr-field-label--success">{t('components.bookings.list.creation-date')}</span>
                                    <span className="bdr-field-value bdr-field-value--success">
                                        {bookingData?.recordDate ? moment(bookingData.recordDate).format(appConfig.config.dateFormat) : '-'}
                                    </span>
                                </div>
                                <div className="bdr-field">
                                    <span className={`bdr-field-label${bookingData?.isCancelled ? ' bdr-field-label--danger' : ''}`}>
                                        {t('components.bookings.list.cancel-at', ['']).replace('<strong>', '').replace('</strong>', '').trim()}
                                    </span>
                                    <span className={`bdr-field-value${bookingData?.isCancelled && (bookingData.cancelledDate || bookingData.lastUpdateDate) ? ' bdr-field-value--danger' : ''}`}>
                                        {bookingData?.isCancelled && (bookingData.cancelledDate || bookingData.lastUpdateDate)
                                            ? moment(bookingData.cancelledDate ?? bookingData.lastUpdateDate).format(appConfig.config.dateFormat)
                                            : '-'}
                                    </span>
                                </div>
                            </div>

                            {/* Row 2: check-in + check-out */}
                            <div className="bdr-dates-grid bdr-dates-grid--separator">
                                <div className="bdr-field">
                                    <span className="bdr-field-label">{t('pages.booking-detail.check-in')}</span>
                                    <span className="bdr-field-value bdr-field-value--date">{moment(bookingData?.bookingLines[0]?.checkIn).format(appConfig.config.dateFormat)}</span>
                                </div>
                                <div className="bdr-field">
                                    <span className="bdr-field-label">{t('pages.booking-detail.check-out')}</span>
                                    <span className="bdr-field-value bdr-field-value--date">{moment(bookingData?.bookingLines[0]?.checkOut).format(appConfig.config.dateFormat)}</span>
                                </div>
                            </div>

                            {/* Row 3: habitaciones + paxes */}
                            <div className="bdr-dates-grid bdr-dates-grid--separator">
                                <div className="bdr-field">
                                    <span className="bdr-field-label">{t('pages.booking-detail.rooms-title')}</span>
                                    <span className="bdr-field-value">{bookingData?.bookingLines.length ?? 0}</span>
                                </div>
                                <div className="bdr-field">
                                    <span className="bdr-field-label">{t('pages.booking-detail.paxes')}</span>
                                    <span className="bdr-field-value">
                                        {(() => {
                                            const adults = bookingData?.bookingLines.reduce((acc, bl) => acc + bl.paxes.filter(p => mapPaxType(p.paxType) === PaxTypeEnum.adult).length, 0) ?? 0;
                                            const minors = bookingData?.bookingLines.reduce((acc, bl) => acc + bl.paxes.filter(p => mapPaxType(p.paxType) !== PaxTypeEnum.adult).length, 0) ?? 0;
                                            return minors > 0 ? `${adults} ad. / ${minors} ni.` : `${adults} adultos`;
                                        })()}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── Owner data ────────────────────────────────────── */}
                    <div className="bdr-card">
                        <div className="bdr-card-title">{t('pages.booking-detail.owner-data.title')}</div>
                        <div className="bdr-fields bdr-fields--row">
                            <div className="bdr-field">
                                <span className="bdr-field-label">{t('pages.booking-detail.owner-data.name')}</span>
                                <span className="bdr-field-value">
                                    {[bookingData?.ownerData.name, bookingData?.ownerData.surname, bookingData?.ownerData.secondSurname].map(safe).filter(Boolean).join(' ') || '-'}
                                </span>
                            </div>
                            <div className="bdr-field">
                                <span className="bdr-field-label">{t('pages.booking-detail.owner-data.phone')}</span>
                                <span className="bdr-field-value">{safe(bookingData?.ownerData.phone) ?? '-'}</span>
                            </div>
                            <div className="bdr-field">
                                <span className="bdr-field-label">{t('pages.booking-detail.owner-data.email')}</span>
                                <span className="bdr-field-value">
                                    {safe(bookingData?.ownerData.email)
                                        ? <a className="app-link" href={`mailto:${bookingData!.ownerData.email}`}>{bookingData!.ownerData.email}</a>
                                        : '-'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* ── Economic summary ──────────────────────────────── */}
                    <div className="bdr-card">
                        <div className="bdr-card-header-row">
                            <div className="bdr-card-title">{t('pages.booking-detail.total-resume.title')}</div>
                            <span className={`bdr-price-type-pill ${isNetPrice ? 'bdr-price-type-net' : 'bdr-price-type-pvp'}`}>
                                {isNetPrice ? t('pages.booking-detail.total-resume.total-net') : t('pages.booking-detail.total-resume.total-pvp')}
                            </span>
                        </div>

                        {(bookingData?.bankName || bookingData?.orderIdTransactionTPV) && (
                            <div className="bdr-econ-meta">
                                {safe(bookingData?.bankName) && (
                                    <span><strong>{t('pages.booking-detail.bank-name')}</strong> {bookingData!.bankName}</span>
                                )}
                                {safe(bookingData?.orderIdTransactionTPV) && (
                                    <span><strong>{t('pages.booking-detail.id-payment')}</strong> {bookingData!.orderIdTransactionTPV}</span>
                                )}
                            </div>
                        )}

                        <div className="bdr-econ-table">
                            <div className="bdr-econ-row">
                                <span className="bdr-econ-label">{t('pages.booking-detail.total-resume.total-pvp')}</span>
                                <span className="bdr-econ-val">{pvp?.toFixed(2)} {bookingData?.currencyCode}</span>
                            </div>
                            <div className="bdr-econ-row bdr-econ-row--commission">
                                <span className="bdr-econ-label">{t('pages.booking-detail.total-resume.total-commission')}</span>
                                <span className="bdr-econ-val">{commission?.toFixed(2)} {bookingData?.currencyCode}</span>
                            </div>
                            <div className="bdr-econ-row bdr-econ-row--total">
                                <span className="bdr-econ-label">{t('pages.booking-detail.total-resume.total-net')}</span>
                                <span className="bdr-econ-val">{net?.toFixed(2)} {bookingData?.currencyCode}</span>
                            </div>
                            {bookingData?.statusName === 'active' && (
                                <>
                                    <div className="bdr-econ-divider" />
                                    <div className="bdr-econ-row">
                                        <span className="bdr-econ-label">{t('components.bookings.list.payed')}</span>
                                        <span className="bdr-econ-val">{bookingData.payed} {bookingData.currencyCode}</span>
                                    </div>
                                    <div className="bdr-econ-row bdr-econ-row--danger">
                                        <span className="bdr-econ-label">{t('components.bookings.list.to-be-payed')}</span>
                                        <span className="bdr-econ-val">{(bookingData.totalToPay ?? 0) - (bookingData.payed ?? 0)} {bookingData.currencyCode}</span>
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="bdr-net-note">
                            {HTMLReactParser(t('pages.booking-detail.total-resume.explain-if-net-or-pvp', [
                                isNetPrice ? t('pages.booking-detail.total-resume.total-net') : t('pages.booking-detail.total-resume.total-pvp')
                            ]))}
                        </div>
                           <div className="bdr-net-note">
                            {HTMLReactParser(t('pages.booking-detail.total-resume.payment-method', [
                                bookingData?.paymentMethodName 
                            ]))}
                        </div>

                        {/* Payment details for pending_payment */}
                        {bookingData?.statusName === 'pending_payment' && (
                            <div className="bdr-payment-section">
                                <div className="bdr-card-title bdr-card-title--sub">{t('pages.booking-detail.payment-info')}</div>
                                <div className="bdr-fields bdr-fields--row">
                                    <div className="bdr-field">
                                        <span className="bdr-field-label">{t('pages.booking-detail.status')}</span>
                                        <span className="bdr-field-value" style={{ color: '#722ed1', fontWeight: 600 }}>{t('pages.booking-detail.pending')}</span>
                                    </div>
                                    <div className="bdr-field">
                                        <span className="bdr-field-label">{t('pages.booking-detail.payment-method')}</span>
                                        <span className="bdr-field-value">{t('pages.booking-detail.credit-card')}</span>
                                    </div>
                                </div>
                                <div className="bdr-payment-actions">
                                    {!isBankDataVisible ? (
                                        <Button
                                            loading={isLoadingBankData}
                                            disabled={isLoadingBankData}
                                            className="app-button"
                                            onClick={handleToggleBankData}
                                            icon={<EyeOutlined />}
                                        >
                                            {t('button.view-details-and-complete')}
                                        </Button>
                                    ) : (
                                        <>
                                            {transactionId && (
                                                <ShowCardPCIProxy transactionId={transactionId} expiryDate={bookingData?.expirationDateCard} />
                                            )}
                                            <div className="bdr-payment-btn-row">
                                                <Button
                                                    className="app-button"
                                                    onClick={completePayment}
                                                    loading={loadingCompletePayment}
                                                    disabled={loadingCompletePayment}
                                                >
                                                    {t('button.complete-payment')}
                                                </Button>
                                                <Button className="app-white-button" onClick={handleToggleBankData} icon={<EyeInvisibleOutlined />}>
                                                    {t('button.hide')}
                                                </Button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ── Rooms ─────────────────────────────────────────── */}
                    <div className="bdr-section" data-tag="rooms">
                        <h3 className="bdr-section-title">{t('pages.booking-detail.rooms-title')}</h3>
                        <div className="bdr-rooms">
                            {bookingData?.bookingLines.map((bookingLine: BookingRoom, index: number) => (
                                <div key={index} className="bdr-room-card">
                                    {bookingLine.mainImage && (
                                        <div className="bdr-room-image">
                                            <Image
                                                src={bookingLine.mainImage}
                                                alt={bookingLine.name}
                                                width="100%"
                                                height={140}
                                                style={{ objectFit: 'cover', display: 'block' }}
                                                preview={{ mask: <ZoomInOutlined style={{ fontSize: 18 }} /> }}
                                            />
                                        </div>
                                    )}
                                    <div className="bdr-room-info">
                                        <h4 className="bdr-room-name">
                                            {HTMLReactParser(t('pages.booking-detail.room-for', [
                                                index + 1,
                                                bookingLine.name,
                                                bookingLine.paxes.filter((p: BookingPax) => mapPaxType(p.paxType) === PaxTypeEnum.adult).length,
                                                bookingLine.paxes.filter((p: BookingPax) => mapPaxType(p.paxType) !== PaxTypeEnum.adult).length
                                            ]))}
                                        </h4>
                                        <div className="bdr-room-meta">
                                            {bookingLine.regimeName && <span className="bdr-room-tag">{bookingLine.regimeName}</span>}
                                            {bookingLine.rateCode && <span className="bdr-room-tag">{bookingLine.rateCode}</span>}
                                            {bookingLine.rateName && <span className="bdr-room-tag">{bookingLine.rateName}</span>}
                                        </div>
                                        <div className="bdr-room-price-inline">
                                            <span className="bdr-room-price-val">
                                                {isNetPrice ? bookingLine.net?.toFixed(2) : bookingLine.pvp?.toFixed(2)} {bookingData.currencyCode}
                                            </span>
                                            <span className="bdr-room-commission">Com. {bookingLine.commission?.toFixed(2)} {bookingData.currencyCode}</span>
                                        </div>
                                        <CancellationPolicies cancellationPolicies={bookingLine.cancellationPolicies} currencyCode={bookingData.currencyCode} checkIn={bookingLine.checkIn} />
                                        <Occupation occupation={bookingLine.paxes} checkOut={bookingLine.checkOut} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* ── Extras ──────────────────────────────────────── */}
                    {bookingData?.extras && bookingData.extras.length > 0 && (
                        <div className="bdr-section" data-tag="extras">
                            <h3 className="bdr-section-title">{t('pages.booking-detail.extra-services.title')}</h3>
                            <div className="bdr-rooms">
                                {bookingData.extras.map((extra: Extra, index: number) => (
                                    <div key={index} className="bdr-room-card">
                                        {extra.image && (
                                            <div className="bdr-room-image">
                                                <Image
                                                    src={extra.image}
                                                    alt={extra.description}
                                                    width="100%"
                                                    height={140}
                                                    style={{ objectFit: 'cover', display: 'block' }}
                                                    preview={{ mask: <ZoomInOutlined style={{ fontSize: 18 }} /> }}
                                                />
                                            </div>
                                        )}
                                        <div className="bdr-room-info">
                                            <h4 className="bdr-room-name">{index + 1}. {extra.description}</h4>
                                            <div className="bdr-fields">
                                                <div className="bdr-field">
                                                    <span className="bdr-field-label">{t('pages.booking-detail.extra-services.quantity')}</span>
                                                    <span className="bdr-field-value">{extra.quantity}</span>
                                                </div>
                                                <div className="bdr-field">
                                                    <span className="bdr-field-label">{t('pages.booking-detail.extra-services.date')}</span>
                                                    <span className="bdr-field-value">
                                                        {moment(extra.applicationDateFrom).format(appConfig.config.dateFormat)} – {moment(extra.applicationDateTo).format(appConfig.config.dateFormat)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="bdr-room-price">
                                            <span className="bdr-room-price-val">
                                                {extra.price > 0 ? `${extra.price.toFixed(2)} ${bookingData.currencyCode}` : t('pages.booking-detail.extra-services.free')}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                </div>
            )}
        </div>
    );
}

export default BookingDetailPage;